import { memo, useState, useEffect, useCallback, useMemo } from 'react'
import { Plus } from 'lucide-react'
import { useStore, useActiveChannelId, useActiveGrid, useAgendaView, useAgendaFocus, useGridUpdateSeq, useHasPermission } from '../../store/index.js'
import { useAgendaItems, useScheduleActions } from '../../hooks/useSchedule.js'
import { useGridList } from '../../hooks/useSchedule.js'
import type { RebroadcastReport, ScheduleItem } from '../../types/index.js'
import { AgendaToolbar }  from './AgendaToolbar.js'
import { AgendaCanvas }   from './AgendaCanvas.js'
import { PlaylistView }   from './PlaylistView.js'
import { WeekView, getWeekMonday } from './WeekView.js'
import { MonitorPanel }   from '../monitor/MonitorPanel.js'
import { ItemDetailPanel } from './ItemDetailPanel.js'
import { AddItemModal }   from './AddItemModal.js'
import { GapFillDialog, type GapFillTarget }  from './GapFillDialog.js'
import { AutoGapFillDialog }                  from './AutoGapFillDialog.js'
import { GapFillRunsBanner }                  from './GapFillRunsBanner.js'
import { AlternatingAdDialog } from './AlternatingAdDialog.js'
import { LiveBreakTemplateDialog } from './LiveBreakTemplateDialog.js'
import { TemplateLoadDialog } from './TemplateLoadDialog.js'
import { GapBanner } from './GapBanner.js'
import { OverlapBanner } from './OverlapBanner.js'
import { MediaIssueBanner } from './MediaIssueBanner.js'
import { ClockChangeBanner } from './ClockChangeLayer.js'
import { type GridGap } from '../../lib/grid-gaps.js'
import { findDayGaps } from '../../lib/night-tail.js'
import { findGridOverlaps, type GridOverlap } from '../../lib/grid-overlaps.js'
import { findGridMediaIssues, type GridMediaIssue } from '../../lib/grid-media-issues.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { useGridUndo } from '../../hooks/useGridUndo.js'
import { formatTime, scheduleItemTitle } from '../../lib/utils.js'
import { ensureWeeklyGrid } from '../../lib/ensureWeeklyGrid.js'
import { addBroadcastDays, startOfBroadcastDay, broadcastDayKey, broadcastDayWindow } from '../../lib/broadcast-day.js'
import { gridsApi, itemsApi, recurrenceApi } from '../../lib/api.js'
import { infoservicesApi } from '../../lib/adsApi.js'
import { ContextMenu, isEditableKeyboardTarget } from '../common/ContextMenu.js'
import { ScheduleItemEditDialog } from './ScheduleItemEditDialog.js'
import { RelatedEditionDialog } from './RelatedEditionDialog.js'
import { ReplaceProgramDialog } from './ReplaceProgramDialog.js'
import { ReplaceJingleDialog } from './ReplaceJingleDialog.js'
import { RebroadcastDialog } from './RebroadcastDialog.js'
import { GridSelectionBar } from './GridSelectionBar.js'
import { useGridSelection, type GridSelectMode } from './useGridSelection.js'
import {
  infoserviceInsertPlansAt, infoserviceRuns,
  type InfoserviceInsertPlan,
} from './infoserviceResize.js'
import { agendaEdgeHasGap } from './edgeAction.js'
import {
  applyInsertion, findDuplicateInsertions, findProgramPairDuplicateInsertions, programPairAroundInsertedTechnicalItem,
  type DuplicateInsertion, type InsertionRecipe,
} from './insertion.js'
import type { AddGap } from './gapOverflow.js'
import { DuplicateInsertionDialog } from './DuplicateInsertionDialog.js'
import './AgendaView.css'
import './AgendaEditors.css'
import { Spinner } from '../common/Spinner.js'
import { LiveSourceSwitchDialog } from '../monitor/LiveSourceSwitchDialog.js'
import { canSwitchLiveSource } from '../../lib/liveSourceSwitch.js'

interface AgendaViewProps {
  defaultAdd?:          { programId: string; itemType: 'PROGRAM' | 'JINGLE' }
  onDefaultAddConsumed?: () => void
}

export const AgendaView = memo(function AgendaView({ defaultAdd, onDefaultAddConsumed }: AgendaViewProps) {
  const canCreate = useHasPermission('GRID_ITEM_CREATE')
  const canEdit = useHasPermission('GRID_ITEM_EDIT')
  const canMove = useHasPermission('GRID_ITEM_MOVE')
  const canDelete = useHasPermission('GRID_ITEM_DELETE')
  const canUseTemplates = useHasPermission('GRID_TEMPLATE_MANAGE')
  const canUseAi = useHasPermission('GRID_AI_MANAGE')
  const canUseAds = useHasPermission('ADS_MANAGE')
  // Le décrochage préfait s'enregistre sur une route /playout, qui exige
  // PLAYOUT_CONTROL en écriture : le bouton doit être gardé par la même
  // permission, sinon il ne mène qu'à un 403 au moment d'enregistrer.
  const canControlPlayout = useHasPermission('PLAYOUT_CONTROL')
  const activeChannelId  = useActiveChannelId()
  useGridList(activeChannelId)
  const { items, loading, reload: reloadItems, nightTail } = useAgendaItems()
  const { deleteItem } = useScheduleActions()

  const activeGrid = useActiveGrid()
  const agendaView = useAgendaView()
  const agendaFocus = useAgendaFocus()

  const [selectedItem, setSelectedItem] = useState<ScheduleItem | null>(null)
  const [editingItem, setEditingItem] = useState<ScheduleItem | null>(null)
  const [relatedItem, setRelatedItem] = useState<ScheduleItem | null>(null)
  const [replacementItem, setReplacementItem] = useState<ScheduleItem | null>(null)
  /** Direct à l'antenne dont on change le flux (clic droit › « Changer de flux… »). */
  const [sourceSwitchItem, setSourceSwitchItem] = useState<ScheduleItem | null>(null)
  const [jingleReplacementItem, setJingleReplacementItem] = useState<ScheduleItem | null>(null)
  const [itemMenu, setItemMenu] = useState<{ item: ScheduleItem; x: number; y: number } | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [addModalInitialDate, setAddModalInitialDate] = useState<string | undefined>()
  const [addModalInitialTime, setAddModalInitialTime] = useState<string | undefined>()
  const [addModalAnchorMode,  setAddModalAnchorMode]  = useState<'START' | 'END'>('START')
  const [addModalPlans,       setAddModalPlans]       = useState<InfoserviceInsertPlan[]>([])
  const [addModalGap,         setAddModalGap]         = useState<AddGap | null>(null)
  const [duplicateOffer,      setDuplicateOffer]      = useState<
    { recipe: InsertionRecipe; gridId: string; duplicates: DuplicateInsertion[] } | null
  >(null)
  const [gridLocked,   setGridLocked]   = useState(true)
  const [showAlternatingAdDialog, setShowAlternatingAdDialog] = useState(false)
  const [showLiveBreakTemplate, setShowLiveBreakTemplate] = useState(false)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [adBlockGridId, setAdBlockGridId] = useState<string | null>(null)
  const [gapToFill, setGapToFill] = useState<GapFillTarget | null>(null)
  const [gapFillChoices, setGapFillChoices] = useState<GapFillTarget[]>([])
  const [gapsToFillAll, setGapsToFillAll] = useState<GapFillTarget[] | null>(null)
  const [focusRequest, setFocusRequest] = useState<{ iso: string; seq: number } | null>(null)
  const [pendingSelectId, setPendingSelectId] = useState<string | null>(null)
  const [shiftHeld, setShiftHeld] = useState(false)
  const [showRebroadcast, setShowRebroadcast] = useState(false)
  const [selectionBusy, setSelectionBusy] = useState(false)

  const gridUndo = useGridUndo()
  // Les passes de comblage se relisent à chaque écriture de grille : un retrait
  // fait ailleurs, ou une seconde passe, doivent se voir sans recharger la page.
  const gridUpdateSeq = useGridUpdateSeq()

  // La pile d'annulation décrit des grilles d'une chaîne : elle n'a aucun sens
  // sur la suivante, et rejouer ses photos y toucherait des éléments étrangers.
  useEffect(() => { useStore.getState().clearGridUndo() }, [activeChannelId])

  // Sélection multiple : disponible en permanence, cadenas ouvert ou fermé.
  // Désigner une tranche d'antenne ne modifie rien ; c'est le geste qui précède
  // toute reprise de grille en direct.
  const selection = useGridSelection(items)

  // Trous de la journée, calculés depuis les éléments déjà en mémoire. Celui de
  // fin de soirée court jusqu'au premier élément du lendemain (`lib/night-tail.ts`).
  const gaps = useMemo(
    () => findDayGaps(items, nightTail, broadcastDayWindow(agendaView.date).to.getTime(), activeGrid?.id ?? null),
    [activeGrid?.id, agendaView.date, items, nightTail],
  )

  const handleFillGap = useCallback((gap: GridGap, availableGaps: GridGap[]) => {
    const toTarget = (candidate: GridGap): GapFillTarget => ({
      gapSec:        candidate.durationSec,
      gapStart:      candidate.startTime,
      gapEnd:        candidate.endTime,
      gridId:        candidate.gridId,
      previousItemTitle: candidate.previousItemTitle,
      nextItemTitle: candidate.nextItemTitle,
    })
    setGapFillChoices(availableGaps.map(toTarget))
    setGapToFill({
      gapSec:        gap.durationSec,
      gapStart:      gap.startTime,
      gapEnd:        gap.endTime,
      gridId:        gap.gridId,
      previousItemTitle: gap.previousItemTitle,
      nextItemTitle: gap.nextItemTitle,
    })
  }, [])

  const handleFillAllGaps = useCallback((availableGaps: GridGap[]) => {
    setGapsToFillAll(availableGaps.map(candidate => ({
      gapSec:        candidate.durationSec,
      gapStart:      candidate.startTime,
      gapEnd:        candidate.endTime,
      gridId:        candidate.gridId,
      previousItemTitle: candidate.previousItemTitle,
      nextItemTitle: candidate.nextItemTitle,
    })))
  }, [])

  // La pose ne ferme pas la fenêtre : elle bascule sur son compte rendu, d'où
  // le retrait de la passe est atteignable d'un clic. La grille, elle, est déjà
  // à jour derrière.
  const handleAutoGapFillApplied = useCallback(() => {
    useStore.getState().incrementGridUpdateSeq()
    reloadItems()
  }, [reloadItems])

  const handleAutoGapFillDone = useCallback((message: string) => {
    setGapsToFillAll(null)
    const store = useStore.getState()
    store.incrementGridUpdateSeq()
    if (message) store.pushToast(message, 'success', 5500)
    reloadItems()
  }, [reloadItems])

  const handleShowGap = useCallback((gap: GridGap) => {
    useStore.getState().setAgendaView('agenda')
    setFocusRequest(current => ({ iso: gap.startTime, seq: (current?.seq ?? 0) + 1 }))
  }, [])

  // Chevauchements de la journée affichée, calculés comme les trous depuis la
  // grille en mémoire : la barre est juste après un rechargement et s'efface
  // d'elle-même une fois le dernier réglé.
  const overlaps = useMemo(() => findGridOverlaps(items, agendaView.date), [items, agendaView.date])

  /**
   * Recalage serveur, encadré pour l'annulation. `label` nomme le geste sur le
   * bouton « Annuler » ; le compte rendu signale ce qui n'a pas pu bouger.
   */
  const runOverlapShift = useCallback(async (
    label: string,
    gridId: string,
    target: { overItemId: string; itemId: string } | { date: string },
  ) => {
    const store = useStore.getState()
    try {
      const result = await recordGridChange({
        label,
        gridIds: [gridId, activeGrid?.id],
        date:    agendaView.date,
        gridIdsFromResult: shift => shift.gridIds,
      }, () => gridsApi.shiftOverlaps(gridId, target))
      store.incrementGridUpdateSeq()
      reloadItems()
      const jingles = result.jingles > 0 ? `${result.jingles} jingle(s) de liaison posé(s)` : null
      const skipped = result.skippedJingles.map(skip => `pas de jingle entre « ${skip.beforeTitle} » et « ${skip.afterTitle} » : `
        + (skip.reason === 'DUPLICATE'
          ? `il pousserait « ${skip.blockedTitle} » sur un doublon`
          : `il pousserait sur « ${skip.blockedTitle} », déjà chargé ou à l’antenne`))
      if (result.blocked.length > 0 || skipped.length > 0) {
        const reasons = [
          ...result.blocked.map(block => block.reason === 'DUPLICATE'
            ? `« ${block.title} » est un doublon, à retirer`
            : `« ${block.title} » est déjà chargé ou à l’antenne`),
          ...skipped,
        ]
        const done = [result.moved > 0 ? `${result.moved} élément(s) décalé(s)` : null, jingles].filter(Boolean).join(', ')
        store.pushToast(`${done ? `${done}, mais ` : ''}${reasons.join(' ; ')}`, 'warning')
      } else if (result.moved > 0) {
        store.pushToast([`${result.moved} élément(s) décalé(s)`, jingles].filter(Boolean).join(', '), 'success')
      } else {
        store.pushToast('Aucun élément à décaler', 'info')
      }
    } catch (cause: any) {
      store.pushToast(cause?.message ?? 'Recalage impossible', 'error')
    }
  }, [activeGrid?.id, agendaView.date, reloadItems])

  const handleShiftOverlap = useCallback((overlap: GridOverlap) => runOverlapShift(
    `recalage de ${overlap.itemTitle}`,
    overlap.gridId,
    { overItemId: overlap.overItemId, itemId: overlap.itemId },
  ), [runOverlapShift])

  const handleShiftAllOverlaps = useCallback(async () => {
    const gridId = overlaps[0]?.gridId ?? activeGrid?.id
    if (!gridId) return
    await runOverlapShift('recalage des chevauchements du jour', gridId, { date: agendaView.date })
  }, [activeGrid?.id, agendaView.date, overlaps, runOverlapShift])

  const handleShowOverlap = useCallback((overlap: GridOverlap) => {
    useStore.getState().setAgendaView('agenda')
    setFocusRequest(current => ({ iso: overlap.startTime, seq: (current?.seq ?? 0) + 1 }))
    setPendingSelectId(overlap.overItemId)
  }, [])

  // Cases dont le fichier n'est pas celui de l'émission ou ne remplit pas la
  // case (`lib/grid-media-issues.ts`), calculées comme les chevauchements.
  const mediaIssues = useMemo(() => findGridMediaIssues(items, agendaView.date), [items, agendaView.date])

  const handleShowMediaIssue = useCallback((issue: GridMediaIssue) => {
    useStore.getState().setAgendaView('agenda')
    setFocusRequest(current => ({ iso: issue.startTime, seq: (current?.seq ?? 0) + 1 }))
    setPendingSelectId(issue.itemId)
  }, [])

  useEffect(() => { if (defaultAdd && canCreate) setShowAddModal(true) }, [canCreate, defaultAdd])
  useEffect(() => {
    if (selectedItem && !items.find(i => i.id === selectedItem.id)) setSelectedItem(null)
  }, [items, selectedItem])

  const { select: selectItemInGrid, clear: clearSelection } = selection

  /** Ouverture d'une fiche depuis le monitor ou le panneau : sélection simple. */
  const handleSelectItem = useCallback((item: ScheduleItem | null) => {
    setSelectedItem(item)
    if (item) selectItemInGrid(item, 'REPLACE')
    else clearSelection()
  }, [clearSelection, selectItemInGrid])

  // Demande de focus « Voir » (notification ou historique) : on
  // repositionne la vue sur le bon jour d'antenne — ce qui recharge la grille de
  // la semaine et ses items —, on scrolle sur l'instant du recouvrement et on
  // met l'item qui déborde en attente de sélection dès qu'il est chargé.
  useEffect(() => {
    if (!agendaFocus) return
    useStore.getState().setAgendaView('agenda')
    useStore.getState().setAgendaDate(broadcastDayKey(agendaFocus.iso))
    setFocusRequest({ iso: agendaFocus.iso, seq: agendaFocus.seq })
    setPendingSelectId(agendaFocus.itemId)
  }, [agendaFocus])

  // La sélection attend que les items du jour ciblé soient effectivement chargés
  // (le changement de date déclenche un rechargement asynchrone).
  useEffect(() => {
    if (!pendingSelectId) return
    const item = items.find(i => i.id === pendingSelectId)
    if (!item) return
    handleSelectItem(item)
    setPendingSelectId(null)
  }, [items, pendingSelectId, handleSelectItem])

  /**
   * Clic sur la grille. Un clic simple garde le comportement historique — il
   * ouvre le panneau de détail ; Ctrl/Cmd et Maj construisent une sélection et
   * referment ce panneau, qui n'a plus de fiche unique à montrer.
   */
  const handleGridSelect = useCallback((item: ScheduleItem | null, mode: GridSelectMode) => {
    if (!item) {
      clearSelection()
      setSelectedItem(null)
      return
    }
    selectItemInGrid(item, mode)
    setSelectedItem(mode === 'REPLACE' ? item : null)
  }, [clearSelection, selectItemInGrid])

  const handleItemContextMenu = useCallback((item: ScheduleItem, x: number, y: number) => {
    setItemMenu({ item, x, y })
  }, [])

  const removeScheduleItem = useCallback(async (item: ScheduleItem) => {
    if (!canDelete) return
    if (['ON_AIR', 'DONE', 'LOADING'].includes(item.status)) {
      window.alert('Cet élément est en cours, déjà diffusé ou en chargement et ne peut pas être supprimé.')
      return
    }
    const title = item.bundleId
      ? `la partie ${item.program?.partNumber ?? (item.bundleOrder ?? 0) + 1} de « ${scheduleItemTitle(item) ?? 'ce programme'} »`
      : `« ${scheduleItemTitle(item) ?? 'cet élément'} »`
    const suffix = item.recurrenceId ? ' Cette action retire uniquement cette occurrence.' : ''
    if (!window.confirm(`Supprimer ${title} de la grille ?${suffix}`)) return
    try {
      await recordGridChange({
        label:   `suppression de ${scheduleItemTitle(item) ?? 'cet élément'}`,
        gridIds: [item.gridId, activeGrid?.id],
        date:    agendaView.date,
      }, async () => {
        if (item.recurrenceId) await recurrenceApi.deleteOne(item.id)
        else await deleteItem(item.id)
      })
      setSelectedItem(current => current?.id === item.id ? null : current)
      setItemMenu(null)
      useStore.getState().pushToast('Élément supprimé de la grille', 'success', 3500)
      reloadItems()
    } catch (cause: any) {
      window.alert(cause?.message ?? 'Impossible de supprimer cet élément.')
    }
  }, [activeGrid?.id, agendaView.date, canDelete, deleteItem, reloadItems])

  const duplicateScheduleItem = useCallback(async (item: ScheduleItem) => {
    if (!canCreate) return
    try {
      const duplicate = await recordGridChange({
        label:   `duplication de ${scheduleItemTitle(item) ?? 'cet élément'}`,
        gridIds: [item.gridId, activeGrid?.id],
        date:    agendaView.date,
      }, () => itemsApi.duplicate(item.id))
      const store = useStore.getState()
      ;(duplicate.items ?? [duplicate]).forEach(store.upsertItem)
      store.incrementGridUpdateSeq()
      setSelectedItem(duplicate)
      store.pushToast('Copie ajoutée après l’élément source', 'success', 4000)
    } catch (cause: any) {
      window.alert(cause?.message ?? 'Impossible de dupliquer cet élément.')
    }
  }, [activeGrid?.id, agendaView.date, canCreate])

  /**
   * Suppression de la sélection. Les éléments chargés, à l'antenne ou déjà
   * diffusés sont laissés en place — ils portent l'historique de diffusion — et
   * le compte rendu le dit plutôt que de faire échouer l'ensemble du geste.
   */
  const removeSelection = useCallback(async () => {
    if (!canDelete || selection.count === 0) return
    const removable = selection.items.filter(item => !['ON_AIR', 'DONE', 'LOADING'].includes(item.status))
    const protectedCount = selection.count - removable.length
    if (removable.length === 0) {
      window.alert('Les éléments sélectionnés sont en cours, en chargement ou déjà diffusés : aucun ne peut être supprimé.')
      return
    }
    const protectedSuffix = protectedCount > 0
      ? ` ${protectedCount} élément(s) en cours ou déjà diffusé(s) seront conservés.`
      : ''
    if (!window.confirm(`Supprimer ${removable.length} élément(s) de la grille ?${protectedSuffix}`)) return

    setSelectionBusy(true)
    let failed = 0
    try {
      await recordGridChange({
        label:   `suppression de ${removable.length} élément(s)`,
        gridIds: [...removable.map(item => item.gridId), activeGrid?.id],
        date:    agendaView.date,
      }, async () => {
        for (const item of removable) {
          try {
            if (item.recurrenceId) await recurrenceApi.deleteOne(item.id)
            else await deleteItem(item.id)
          } catch (cause) {
            failed += 1
            console.error('[AgendaView] suppression impossible:', cause)
          }
        }
      })
    } finally {
      setSelectionBusy(false)
    }

    clearSelection()
    setSelectedItem(null)
    const store = useStore.getState()
    store.incrementGridUpdateSeq()
    const removed = removable.length - failed
    store.pushToast(
      failed > 0
        ? `${removed} élément(s) supprimé(s) · ${failed} refusé(s) par le serveur`
        : `${removed} élément(s) supprimé(s) de la grille`,
      failed > 0 ? 'warning' : 'success',
      failed > 0 ? 6000 : 3500,
    )
    reloadItems()
  }, [activeGrid?.id, agendaView.date, canDelete, clearSelection, deleteItem, reloadItems, selection.count, selection.items])

  const handleRebroadcastDone = useCallback((report: RebroadcastReport) => {
    const store = useStore.getState()
    // La rediffusion peut ouvrir la grille de la semaine suivante : la liste des
    // grilles doit se rafraîchir, pas seulement les éléments du jour affiché.
    store.incrementGridsUpdateSeq()
    store.incrementGridUpdateSeq()
    clearSelection()
    setSelectedItem(null)
    const endLabel = report.endsAt ? formatTime(report.endsAt).slice(0, 5) : '—'
    const askedLabel = formatTime(report.requestedEnd).slice(0, 5)
    // La boucle écourtée se dit dans le toast : c'est la seule trace, une fois
    // le dialogue fermé, que la dernière copie n'est pas la soirée entière.
    const tailNote = report.partialTail
      ? ` · dernière boucle écourtée à ${report.partialItemCount}/${report.sourceItemCount} éléments`
      : ''
    store.pushToast(
      report.truncated
        ? `${report.occurrences} rediffusion(s) · la dernière s’achève à ${endLabel}, avant ${askedLabel}${tailNote}`
        : `${report.occurrences} rediffusion(s) posée(s) jusqu’à ${askedLabel}${tailNote}`,
      report.truncated ? 'warning' : 'success',
      report.truncated ? 8000 : 5000,
    )
    reloadItems()
  }, [clearSelection, reloadItems])

  const editScheduleItem = useCallback((item: ScheduleItem) => {
    if (!canEdit) return
    if (['ON_AIR', 'DONE', 'LOADING'].includes(item.status)) return
    if (item.itemType === 'AD_BREAK') {
      setSelectedItem(item)
      return
    }
    setSelectedItem(null)
    setEditingItem(item)
  }, [canEdit])

  const handleScheduleItemUpdated = useCallback((item: ScheduleItem) => {
    const store = useStore.getState()
    store.upsertItem(item)
    store.incrementGridUpdateSeq()
    setSelectedItem(item)
  }, [])

  // Maj met le bouton d'ajout en avant. On ecoute aussi blur : sans cela, un
  // changement de fenetre pendant que Maj est enfoncee laisserait le bouton
  // arme indefiniment.
  useEffect(() => {
    const onDown = (event: KeyboardEvent) => { if (event.key === 'Shift') setShiftHeld(true) }
    const onUp = (event: KeyboardEvent) => { if (event.key === 'Shift') setShiftHeld(false) }
    const onBlur = () => setShiftHeld(false)
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  useEffect(() => {
    const dialogOpen = () => Boolean(editingItem || relatedItem || replacementItem || jingleReplacementItem || showRebroadcast || showAddModal)

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isEditableKeyboardTarget(event.target)) return

      // Échap referme la sélection avant tout : c'est la sortie de secours quand
      // on a construit la mauvaise plage au milieu d'une reprise de grille.
      if (event.key === 'Escape' && !dialogOpen() && selection.count > 0) {
        event.preventDefault()
        clearSelection()
        setSelectedItem(null)
        return
      }

      // Ctrl/Cmd + Z : le geste qu'on cherche d'instinct quand un modèle ou une
      // rediffusion vient de poser autre chose que ce qu'on attendait. Avec Maj
      // — ou Ctrl + Y, réflexe de ceux qui viennent de Windows — il rétablit.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !dialogOpen()) {
        event.preventDefault()
        const replay = event.shiftKey ? gridUndo.redo : gridUndo.undo
        replay().catch(console.error)
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y' && !dialogOpen()) {
        event.preventDefault()
        gridUndo.redo().catch(console.error)
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a' && !dialogOpen()) {
        event.preventDefault()
        selection.selectAll()
        setSelectedItem(null)
        return
      }

      if (event.key !== 'Backspace' || dialogOpen()) return
      // La sélection multiple prime : c'est elle que l'opérateur voit surlignée.
      if (selection.count > 1) {
        event.preventDefault()
        removeSelection().catch(console.error)
        return
      }
      if (!selectedItem) return
      event.preventDefault()
      removeScheduleItem(selectedItem).catch(console.error)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [clearSelection, editingItem, gridUndo, jingleReplacementItem, relatedItem, removeScheduleItem, removeSelection, replacementItem, selectedItem, selection, showAddModal, showRebroadcast])

  const handleAddAtTime = useCallback((isoDateString: string, mode: 'START' | 'END' = 'START', plans: InfoserviceInsertPlan[] = [], gap?: AddGap) => {
    const d = new Date(isoDateString)
    if (isNaN(d.getTime())) return
    const yyyy = d.getFullYear()
    const mm   = String(d.getMonth() + 1).padStart(2, '0')
    const dd   = String(d.getDate()).padStart(2, '0')
    setAddModalInitialDate(`${yyyy}-${mm}-${dd}`)
    setAddModalInitialTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`)
    // Le bord cédé par l'infoservice détermine de quel côté de
    // l'intersection se pose l'ajout : fin ancrée quand la réserve est en
    // amont, début ancré quand elle est en aval.
    setAddModalAnchorMode(plans[0]?.edge ?? mode)
    setAddModalPlans(plans)
    setAddModalGap(gap ?? null)
    setShowAddModal(true)
  }, [])

  /**
   * Une insertion vient de prendre du temps à l'infoservice. La même séquence
   * repasse peut-être ailleurs dans la semaine — une soirée rediffusée de nuit,
   * typiquement : on va voir, et on demande avant de toucher quoi que ce soit.
   */
  const handleInserted = useCallback(async (info: {
    plan: InfoserviceInsertPlan
    recipe: InsertionRecipe
    gridId: string
  }) => {
    const monday = getWeekMonday(agendaView.date)
    const weekItems = await gridsApi.getItems(
      info.gridId,
      startOfBroadcastDay(monday).toISOString(),
      addBroadcastDays(monday, 7).toISOString(),
    )
    const duplicates = findDuplicateInsertions(weekItems, info.plan, info.recipe)
    if (duplicates.length === 0) return
    setDuplicateOffer({ recipe: info.recipe, gridId: info.gridId, duplicates })
  }, [agendaView.date])

  const handleInsertedSafe = useCallback((info: {
    plan: InfoserviceInsertPlan
    recipe: InsertionRecipe
    gridId: string
  }) => {
    // Chercher les copies est un service rendu, pas une étape de l'ajout :
    // l'échec de la recherche ne doit pas ressembler à un échec de l'insertion.
    handleInserted(info).catch(error => console.error('[AgendaView] duplications introuvables:', error))
  }, [handleInserted])

  const handleApplyDuplicates = useCallback(async (selected: DuplicateInsertion[]) => {
    if (!duplicateOffer || !activeChannelId) return
    const { recipe } = duplicateOffer
    await recordGridChange({
      label:   `répercussion de ${recipe.label} sur ${selected.length} rediffusion(s)`,
      gridIds: Array.from(new Set(selected.map(entry => entry.gridId))),
      date:    agendaView.date,
    }, async () => {
      for (const entry of selected) {
        await applyInsertion(activeChannelId, entry.gridId, entry.plan, recipe)
      }
    })
    const store = useStore.getState()
    store.incrementGridUpdateSeq()
    reloadItems()
    store.pushToast(`${recipe.label} répercuté sur ${selected.length} rediffusion(s)`, 'success', 4500)
  }, [activeChannelId, agendaView.date, duplicateOffer, reloadItems])

  const handlePropagateExistingTechnicalItem = useCallback(async (item: ScheduleItem) => {
    if (!activeChannelId || !['JINGLE', 'INFOSERVICE'].includes(item.itemType)) return
    const monday = getWeekMonday(agendaView.date)
    const weekItems = await gridsApi.getItems(
      item.gridId,
      startOfBroadcastDay(monday).toISOString(),
      addBroadcastDays(monday, 7).toISOString(),
    )
    const sourceItem = weekItems.find(candidate => candidate.id === item.id) ?? item
    const source = programPairAroundInsertedTechnicalItem(weekItems, sourceItem)
    const store = useStore.getState()
    if (!source) {
      store.pushToast('Impossible de retrouver les deux programmes encadrant cet élément technique.', 'warning', 5500)
      return
    }
    const recipe: InsertionRecipe = item.itemType === 'INFOSERVICE'
      ? {
          kind: 'INFOSERVICE',
          durationSec: item.durationSec,
          label: 'cet infoservice',
        }
      : {
          kind: 'ITEM',
          itemType: 'JINGLE',
          programId: item.programId!,
          ...(item.mediaAssetId ? { mediaAssetId: item.mediaAssetId } : {}),
          durationSec: item.durationSec,
          label: `« ${scheduleItemTitle(item, 'élément d’antenne')} »`,
        }
    const duplicates = findProgramPairDuplicateInsertions(weekItems, source, recipe.durationSec)
    if (duplicates.length === 0) {
      store.pushToast('Aucun autre passage avec ces deux programmes collés n’a été trouvé cette semaine.', 'info', 5500)
      return
    }
    setDuplicateOffer({ recipe, gridId: item.gridId, duplicates })
  }, [activeChannelId, agendaView.date])

  const handleOpenAdBlocks = useCallback(async () => {
    if (!activeChannelId) return
    const grid = activeGrid ?? await ensureWeeklyGrid(activeChannelId, agendaView.date)
    setAdBlockGridId(grid.id)
    setShowAlternatingAdDialog(true)
  }, [activeChannelId, activeGrid, agendaView.date])

  /**
   * Décrochage préfait de la journée affichée. Aucune grille n'est nécessaire :
   * le modèle est rattaché à la journée d'antenne, pas à la grille hebdomadaire.
   */
  const handleOpenLiveBreak = useCallback(() => setShowLiveBreakTemplate(true), [])
  const handleCloseLiveBreak = useCallback(() => setShowLiveBreakTemplate(false), [])

  const handleFillInfoservice = useCallback(async () => {
    if (!activeChannelId) return
    if (!window.confirm(`Remplir automatiquement les trous d’au moins 1 minute du ${agendaView.date} avec l’infoservice sélectionné ?`)) return
    try {
      const grid = activeGrid ?? await ensureWeeklyGrid(activeChannelId, agendaView.date)
      const result = await recordGridChange({
        label:   'remplissage automatique par l’infoservice',
        gridIds: [grid.id],
        date:    agendaView.date,
      }, () => infoservicesApi.fillGaps({
        channelId: activeChannelId,
        gridId: grid.id,
        date: agendaView.date,
        minimumGapSec: 60,
      }))
      const store = useStore.getState()
      store.incrementGridUpdateSeq()
      store.pushToast(
        result.created > 0
          ? `${result.gapCount} trou(s) d’au moins 1 minute rempli(s) avec l’infoservice sélectionné`
          : 'Aucun trou d’au moins 1 minute à remplir pour cette journée',
        result.created > 0 ? 'success' : 'warning',
        4500,
      )
      reloadItems()
    } catch (cause: any) {
      window.alert(cause?.message ?? 'Impossible de remplir avec l’infoservice.')
    }
  }, [activeChannelId, activeGrid, agendaView.date, reloadItems])

  const handleTemplateApplied = useCallback((gridId: string, targetDate: string) => {
    const store = useStore.getState()
    store.setAgendaDate(targetDate)
    store.setActiveGrid(gridId)
    store.incrementGridsUpdateSeq()
    store.incrementGridUpdateSeq()
    setShowTemplateDialog(false)
  }, [])

  const handleAddItem = useCallback(() => {
    setAddModalInitialDate(undefined)
    setAddModalInitialTime(undefined)
    setAddModalAnchorMode('START')
    setAddModalPlans([])
    setAddModalGap(null)
    setShowAddModal(true)
  }, [])
  const handleOpenAdBlocksClick = useCallback(() => { handleOpenAdBlocks().catch(console.error) }, [handleOpenAdBlocks])
  const handleFillInfoserviceClick = useCallback(() => { handleFillInfoservice().catch(console.error) }, [handleFillInfoservice])
  const handleAlternatingAdsApplied = useCallback((created: number) => {
    const store = useStore.getState()
    store.incrementGridUpdateSeq()
    store.pushToast(`${created} spot(s) placé(s) en alternance avant les infoservices`, 'success', 5500)
    reloadItems()
  }, [reloadItems])
  const handleOpenTemplate = useCallback(() => setShowTemplateDialog(true), [])
  const handleToggleLock = useCallback(() => setGridLocked(current => !current), [])
  const handleWeekDayClick = useCallback((date: string) => {
    const store = useStore.getState()
    store.setAgendaDate(date)
    store.setAgendaView('agenda')
  }, [])

  /**
   * Armement de l'ajout, commun a toutes les surfaces qui en proposent un : le
   * bouton flottant et les « + Ajouter » poses aux bords des blocs. Cadenas
   * deverrouille, ou touche Maj maintenue. Une seule expression, sinon les deux
   * surfaces divergent — c'etait le cas : les boutons de bord ignoraient le
   * cadenas et ne repondaient qu'a la permission.
   */
  const isAddArmed = canCreate && (!gridLocked || shiftHeld)

  /**
   * Insertions possibles aux deux bords de l'élément visé par le clic droit.
   *
   * C'est la seule voie pour un jingle : trop court pour porter un bloc, il
   * passe dans la gouttière des liaisons, où il n'y a pas de place pour un
   * bouton de survol. Le menu contextuel, lui, s'ouvre partout de la même
   * façon — et c'est bien avant et après les jingles que se glissent les spots.
   */
  const menuInsert = useMemo(() => {
    if (!itemMenu) return { before: null, after: null }
    const runs = infoserviceRuns(items)
    const startMs = new Date(itemMenu.item.startTime).getTime()
    const endMs = new Date(itemMenu.item.endTime).getTime()
    // Sans voisin collé, le bord n'est pas une intersection : c'est un trou, et
    // l'ajout ordinaire y suffit.
    const glued = (boundaryMs: number, edge: 'endTime' | 'startTime') =>
      !agendaEdgeHasGap(items, itemMenu.item.id, boundaryMs, edge)
    const beforeOccupied = glued(startMs, 'endTime')
    const afterOccupied = glued(endMs, 'startTime')
    return {
      before: beforeOccupied
        ? isAddArmed && canMove ? infoserviceInsertPlansAt(items, runs, startMs) : []
        : null,
      after: afterOccupied
        ? isAddArmed && canMove ? infoserviceInsertPlansAt(items, runs, endMs) : []
        : null,
    }
  }, [canMove, isAddArmed, itemMenu, items])
  const menuInsertBefore = menuInsert.before && menuInsert.before.length > 0 ? menuInsert.before : null
  const menuInsertAfter = menuInsert.after && menuInsert.after.length > 0 ? menuInsert.after : null

  return (
    <div className="agenda-view">
      <div className="agenda-workspace">
        <div className="agenda-schedule">
          <AgendaToolbar
            onOpenAdBlocks={canUseAds && canCreate && canMove ? handleOpenAdBlocksClick : undefined}
            onOpenLiveBreak={canControlPlayout ? handleOpenLiveBreak : undefined}
            onFillInfoservice={canUseAds && canCreate ? handleFillInfoserviceClick : undefined}
            onOpenTemplate={canUseTemplates ? handleOpenTemplate : undefined}
            onSmartGridApplied={canUseAi ? reloadItems : undefined}
            canUseAi={canUseAi}
            locked={gridLocked}
            onToggleLock={handleToggleLock}
          />

          {(agendaView.view === 'agenda' || agendaView.view === 'week') && (
            <ClockChangeBanner date={agendaView.date} view={agendaView.view} />
          )}

          {overlaps.length > 0 && (
            <OverlapBanner
              overlaps={overlaps}
              onShift={handleShiftOverlap}
              onShiftAll={handleShiftAllOverlaps}
              onShow={handleShowOverlap}
              canShift={canMove}
            />
          )}

          {mediaIssues.length > 0 && (
            <MediaIssueBanner issues={mediaIssues} onShow={handleShowMediaIssue} />
          )}

          {gaps.length > 0 && (
            <GapBanner gaps={gaps} onFill={handleFillGap} onShow={handleShowGap} onFillAll={handleFillAllGaps} />
          )}

          {activeChannelId && (
            <GapFillRunsBanner
              channelId={activeChannelId}
              date={agendaView.date}
              refreshKey={gridUpdateSeq}
              onReverted={handleAutoGapFillDone}
            />
          )}

          <GridSelectionBar
            selection={selection}
            busy={selectionBusy}
            onRebroadcast={canCreate ? () => setShowRebroadcast(true) : undefined}
            onDelete={canDelete ? () => { removeSelection().catch(console.error) } : undefined}
          />

          <div className="agenda-stage">
            {agendaView.view === 'agenda' && (
              <>
                <AgendaCanvas
                  selectedItemId={selectedItem?.id ?? null}
                  onSelectItem={handleGridSelect}
                  isMultiSelected={selection.isSelected}
                  multiSelectedIds={selection.selectedIds}
                  onItemContextMenu={handleItemContextMenu}
                  // Sans handler, AgendaBlock ne rend aucun bouton de bord.
                  onAddAtTime={isAddArmed ? handleAddAtTime : undefined}
                  // Le glisser-deposer exige le cadenas ouvert, sans echappatoire
                  // Maj : contrairement a l'ajout, deplacer est un geste tenu, et
                  // maintenir Maj pendant tout le trajet n'aurait rien d'un
                  // garde-fou. Cadenas ferme = grille inerte au pointeur.
                  dragLocked={!canMove || gridLocked}
                  // L'insertion en force, elle, se declenche d'un clic : elle
                  // s'arme donc comme l'ajout (cadenas ouvert ou Maj tenu), a
                  // la permission de deplacement pres — elle décale la chaine
                  // entre l'intersection et l'infoservice choisi.
                  canInsert={isAddArmed && canMove}
                  focusRequest={focusRequest}
                  displayEndMs={nightTail?.endMs ?? null}
                />
                {loading && (
                  <div className="agenda-loading" role="status">
                    <Spinner /> Chargement…
                  </div>
                )}
              </>
            )}

            {agendaView.view === 'playlist' && (
              <PlaylistView
                items={items}
                loading={loading}
                selectedItemId={selectedItem?.id ?? null}
                onSelectItem={handleGridSelect}
                isMultiSelected={selection.isSelected}
                onItemContextMenu={handleItemContextMenu}
                date={agendaView.date}
              />
            )}

            {agendaView.view === 'week' && (
              <WeekView
                date={agendaView.date}
                onDayClick={handleWeekDayClick}
                onItemContextMenu={handleItemContextMenu}
              />
            )}

            {/* Ajout en bouton flottant. De base il nécessite la touche Maj ("Maj + clic pour ajouter").
                Le cadenas dans la barre d'outils permet de le rendre constamment actif sans la touche Maj. */}
            <button
              type="button"
              onClick={handleAddItem}
              disabled={!isAddArmed}
              className={`tp-button tp-button--primary agenda-fab${isAddArmed ? ' is-armed' : ''}`}
              title={isAddArmed
                ? 'Ajouter un élément à la grille'
                : 'Maintenez la touche Maj pour activer l’ajout (ou cliquez sur le cadenas en haut)'}
              aria-keyshortcuts="Shift"
            >
              <Plus size={16} aria-hidden="true" />
              <span className="agenda-fab__label">
                {isAddArmed ? 'Ajouter à la grille' : 'Maj + clic pour ajouter'}
              </span>
            </button>
          </div>
        </div>

        <MonitorPanel channelId={activeChannelId} />
      </div>

      {selectedItem && (
        <ItemDetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onEdit={editScheduleItem}
          onItemUpdated={handleScheduleItemUpdated}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      {editingItem && activeChannelId && (
        <ScheduleItemEditDialog
          item={editingItem}
          channelId={activeChannelId}
          onSaved={handleScheduleItemUpdated}
          onClose={() => setEditingItem(null)}
        />
      )}

      {relatedItem && (
        <RelatedEditionDialog
          item={relatedItem}
          onSaved={item => {
            const store = useStore.getState()
            store.upsertItem(item)
            store.incrementGridUpdateSeq()
            setSelectedItem(item)
            store.pushToast('Programme remplacé par une édition liée', 'success', 4000)
          }}
          onClose={() => setRelatedItem(null)}
        />
      )}

      {replacementItem && activeChannelId && (
        <ReplaceProgramDialog
          item={replacementItem}
          channelId={activeChannelId}
          onSaved={result => {
            const store = useStore.getState()
            store.incrementGridUpdateSeq()
            setSelectedItem(null)
            const occurrenceLabel = result.replacedCount > 1 ? 'occurrences remplacées' : 'occurrence remplacée'
            const skippedLabel = result.skippedCount > 0 ? ` · ${result.skippedCount} occurrence(s) en cours ou passée(s) ignorée(s)` : ''
            store.pushToast(`${result.replacedCount} ${occurrenceLabel} par « ${result.targetProgramTitle} »${skippedLabel}`, result.skippedCount > 0 ? 'warning' : 'success', 5500)
            reloadItems()
          }}
          onClose={() => setReplacementItem(null)}
        />
      )}

      {jingleReplacementItem && activeChannelId && (
        <ReplaceJingleDialog
          item={jingleReplacementItem}
          channelId={activeChannelId}
          onSaved={item => {
            const store = useStore.getState()
            store.upsertItem(item)
            store.incrementGridUpdateSeq()
            setSelectedItem(item)
            store.pushToast(`Élément d’antenne remplacé par « ${item.program?.title ?? 'le contenu choisi'} »`, 'success', 4500)
          }}
          onClose={() => setJingleReplacementItem(null)}
        />
      )}

      {showRebroadcast && selection.count > 0 && activeChannelId && (
        <RebroadcastDialog
          items={selection.items}
          channelId={activeChannelId}
          onDone={handleRebroadcastDone}
          onClose={() => setShowRebroadcast(false)}
        />
      )}

      {showAddModal && activeChannelId && (
        <AddItemModal
          channelId={activeChannelId}
          gridId={activeGrid?.id ?? ''}
          defaultDate={addModalInitialDate ?? agendaView.date}
          defaultTime={addModalInitialTime}
          anchorMode={addModalAnchorMode}
          insertPlans={addModalPlans}
          insertGap={addModalGap}
          canInsert={canMove}
          onInserted={handleInsertedSafe}
          onCreated={reloadItems}
          onClose={() => { setShowAddModal(false); setAddModalPlans([]); setAddModalGap(null); onDefaultAddConsumed?.() }}
          defaultProgramId={defaultAdd?.programId}
          defaultItemType={defaultAdd?.itemType}
        />
      )}

      {duplicateOffer && (
        <DuplicateInsertionDialog
          recipe={duplicateOffer.recipe}
          duplicates={duplicateOffer.duplicates}
          onApply={handleApplyDuplicates}
          onClose={() => setDuplicateOffer(null)}
        />
      )}

      {showTemplateDialog && activeChannelId && (
        <TemplateLoadDialog
          channelId={activeChannelId}
          gridId={activeGrid?.id ?? null}
          date={agendaView.date}
          onApplied={handleTemplateApplied}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}


      {gapToFill && (
        <GapFillDialog
          gap={gapToFill}
          availableGaps={gapFillChoices}
          onGapChange={setGapToFill}
          onDone={() => { setGapToFill(null); setGapFillChoices([]); reloadItems() }}
          onSkip={() => { setGapToFill(null); setGapFillChoices([]) }}
        />
      )}

      {gapsToFillAll && gapsToFillAll.length > 0 && (
        <AutoGapFillDialog
          gaps={gapsToFillAll}
          onApplied={handleAutoGapFillApplied}
          onDone={handleAutoGapFillDone}
          onClose={() => setGapsToFillAll(null)}
        />
      )}

      {showLiveBreakTemplate && activeChannelId && (
        <LiveBreakTemplateDialog
          channelId={activeChannelId}
          day={agendaView.date}
          onClose={handleCloseLiveBreak}
        />
      )}

      {showAlternatingAdDialog && activeChannelId && adBlockGridId && (
        <AlternatingAdDialog
          channelId={activeChannelId}
          gridId={adBlockGridId}
          date={agendaView.date}
          onApplied={handleAlternatingAdsApplied}
          onClose={() => setShowAlternatingAdDialog(false)}
        />
      )}

      {sourceSwitchItem && activeChannelId && (
        <LiveSourceSwitchDialog
          channelId={activeChannelId}
          item={sourceSwitchItem}
          onClose={() => setSourceSwitchItem(null)}
        />
      )}

      {itemMenu && <ContextMenu
        x={itemMenu.x}
        y={itemMenu.y}
        onClose={() => setItemMenu(null)}
        actions={[
          // Direct à l'antenne : la bascule vers le flux de secours passe en tête,
          // c'est le seul geste utile sur un élément en cours.
          ...(canSwitchLiveSource(itemMenu.item) && activeChannelId ? [{
            label: 'Changer de flux…',
            disabled: !canControlPlayout,
            onClick: () => setSourceSwitchItem(itemMenu.item),
          }] : []),
          { label: 'Modifier', disabled: !canEdit || ['ON_AIR', 'DONE', 'LOADING'].includes(itemMenu.item.status), onClick: () => editScheduleItem(itemMenu.item) },
          {
            label: menuInsertBefore
              ? 'Insérer avant cet élément…'
              : menuInsert.before !== null ? 'Insertion impossible avant cet élément' : 'Ajouter avant cet élément…',
            disabled: menuInsert.before !== null ? !menuInsertBefore : !isAddArmed,
            onClick: () => { handleAddAtTime(itemMenu.item.startTime, menuInsertBefore?.[0]?.edge ?? 'END', menuInsertBefore ?? []) },
          },
          {
            label: menuInsertAfter
              ? 'Insérer après cet élément…'
              : menuInsert.after !== null ? 'Insertion impossible après cet élément' : 'Ajouter après cet élément…',
            disabled: menuInsert.after !== null ? !menuInsertAfter : !isAddArmed,
            onClick: () => { handleAddAtTime(itemMenu.item.endTime, menuInsertAfter?.[0]?.edge ?? 'START', menuInsertAfter ?? []) },
          },
          {
            label: 'Remplacer le programme…',
            disabled: !canEdit || itemMenu.item.status !== 'SCHEDULED' || itemMenu.item.itemType !== 'PROGRAM' || !itemMenu.item.programId,
            onClick: () => {
              setSelectedItem(null)
              setReplacementItem(itemMenu.item)
            },
          },
          ...(itemMenu.item.itemType === 'JINGLE' ? [{
            label: 'Remplacer cet élément d’antenne…',
            disabled: !canEdit || itemMenu.item.status !== 'SCHEDULED' || !itemMenu.item.programId,
            onClick: () => {
              setSelectedItem(null)
              setJingleReplacementItem(itemMenu.item)
            },
          }, {
            label: 'Répercuter entre mêmes programmes…',
            disabled: !canCreate || !canMove || itemMenu.item.status !== 'SCHEDULED' || !itemMenu.item.programId,
            onClick: () => {
              setSelectedItem(null)
              handlePropagateExistingTechnicalItem(itemMenu.item).catch(error => {
                console.error('[AgendaView] répercussion impossible:', error)
                useStore.getState().pushToast(error instanceof Error ? error.message : 'Répercussion impossible', 'error', 5500)
              })
            },
          }] : []),
          ...(itemMenu.item.itemType === 'INFOSERVICE' ? [{
            label: 'Répercuter entre mêmes programmes…',
            disabled: !canCreate || !canMove || itemMenu.item.status !== 'SCHEDULED',
            onClick: () => {
              setSelectedItem(null)
              handlePropagateExistingTechnicalItem(itemMenu.item).catch(error => {
                console.error('[AgendaView] répercussion impossible:', error)
                useStore.getState().pushToast(error instanceof Error ? error.message : 'Répercussion impossible', 'error', 5500)
              })
            },
          }] : []),
          {
            label: 'Remplacer par une édition liée…',
            disabled: !canEdit || ['ON_AIR', 'DONE', 'LOADING'].includes(itemMenu.item.status) || itemMenu.item.itemType !== 'PROGRAM' || !itemMenu.item.programId,
            onClick: () => {
              setSelectedItem(null)
              setRelatedItem(itemMenu.item)
            },
          },
          { label: 'Dupliquer après cet élément', disabled: !canCreate || ['ON_AIR', 'DONE', 'LOADING'].includes(itemMenu.item.status), onClick: () => { duplicateScheduleItem(itemMenu.item).catch(console.error) } },
          {
            // Sur un élément hors sélection, le clic droit vaut désignation :
            // rediffuser la plage d'un autre bloc que celui visé serait un piège.
            label: selection.isSelected(itemMenu.item.id) && selection.count > 1
              ? `Rediffuser la sélection (${selection.count})…`
              : 'Rediffuser cet élément…',
            disabled: !canCreate,
            onClick: () => {
              if (!selection.isSelected(itemMenu.item.id)) selectItemInGrid(itemMenu.item, 'REPLACE')
              setSelectedItem(null)
              setShowRebroadcast(true)
            },
          },
          { label: 'Supprimer', danger: true, disabled: !canDelete || ['ON_AIR', 'DONE', 'LOADING'].includes(itemMenu.item.status), onClick: () => { removeScheduleItem(itemMenu.item).catch(console.error) } },
        ]}
      />}
    </div>
  )
})
